// Public FAQ page for the pilot. Routed at /faq, OUTSIDE the access-code
// gate so it's readable without an invite (same posture as /terms and
// /privacy). Operational, for someone who's in or about to run: what they
// get, how long it takes, what happens to their data, who to contact.
//
// Deliberately short. The Privacy Policy and Terms remain the source of
// truth for retention/legal detail — these answers link out rather than
// restating, so the retention promise lives in one place and can't drift.
//
// Copy reviewed by Rich 2026-06-09 (docs/topics/faq/draft.md). Two claims
// here have live dependencies — keep them honest if the product changes:
//   - "we'll email you the moment your brief is ready" assumes the
//     ready-email flag (ready_email_enabled) is ON. It ships defaulting OFF
//     until the operator flips it post-deploy. Don't ship /faq publicly
//     ahead of that flip, or this over-promises.
//   - retention windows (30-day provider / 7-day come-back) mirror the
//     Privacy page; if that changes, change it there and re-check here.

import type { ReactNode } from "react";
import { CloudMast } from "./CloudMast";

const CONTACT = "hello@plaintosee.cc";
const DATA_CONTACT = "terms@plaintosee.cc";
const SAMPLE_URL = "https://plaintosee.cc/sample-analysis";

// Native <details> accordion — keyboard-accessible and JS-free. The first
// item is open by default so the page reads as content, not a cold list.
function FaqItem({
  q,
  open,
  children,
}: {
  q: string;
  open?: boolean;
  children: ReactNode;
}) {
  return (
    <details className="faq-item" open={open}>
      <summary className="faq-q">{q}</summary>
      <div className="faq-a">{children}</div>
    </details>
  );
}

export function FaqPage() {
  return (
    <main className="landing legal-page faq-page">
      <div className="landing-wrap">
        <CloudMast
          right={
            <a className="legal-back" href="/">
              ← Back to Aurora
            </a>
          }
        />

        <article className="legal-article faq-article">
          <div className="landing-eyebrow">Help</div>
          <h1 className="landing-title">Frequently asked questions</h1>
          <p className="legal-lede">
            Quick answers about running your analysis and what happens to your
            data. For the full detail, see the{" "}
            <a href="/privacy">Privacy Policy</a> and <a href="/terms">Terms</a>
            .
          </p>

          <FaqItem q="What will I get at the end?" open>
            <p>
              An encrypted document you open in your browser with a passphrase
              shown to you once. It names each pattern Aurora found and links
              every claim to the messages behind it, so you (or your attorney)
              can check the source for anything. From the document you can also
              export to Microsoft Word, formatted so the text drops cleanly into
              a declaration or other document. You keep the file — it stays on
              your device unless you choose to send it.
            </p>
          </FaqItem>

          <FaqItem q="Why don't I get an account?">
            <p>
              On purpose. An account means an email, a password, and a
              standing record that you signed up — the wrong thing to ask
              when your inbox or your devices aren't fully yours to control.
              Starting a run gives you an access code instead — what your
              payment attaches to, and how you get back in — plus a
              passphrase that locks your finished file. Save both somewhere
              only you can find them, like a password manager or a private
              note; guard the passphrase especially, since we don't keep a
              copy of it, and if it's lost, even we can't reopen your file
              for you.
            </p>
          </FaqItem>

          <FaqItem q="How long does the analysis take?">
            <p>
              Usually 15–30 minutes — it depends on the size of your record
              and how busy the AI service is at the time, so it can run
              longer. Either way, you don't wait on the page for it: once
              you've started the run you can close the tab, and we'll email
              you the moment your document is ready.
            </p>
          </FaqItem>

          <FaqItem q="How many patterns will it find?">
            <p>
              There's no typical number, and we can't promise one. Some records
              surface many patterns; some surface only a few, or none at all.
              Finding little or nothing isn't a failure — Aurora won't invent
              patterns to fill space, and a quiet record is itself information.
              The document is a single web page (not a set number of printed
              pages): a short summary, then one section per pattern Aurora
              found, each with its cited messages. You can see the shape of the
              output in the <a href={SAMPLE_URL}>example analysis</a> on the
              site.
            </p>
          </FaqItem>

          <FaqItem q="Can this help me meet my court's page limit for filing?">
            <p>
              Often, yes — it's one of the more useful things it does. Courts
              cap how long a filing can be, and a raw message history can run to
              hundreds or thousands of pages. Aurora distills that into a
              focused, cited document, so you and your attorney can build a
              filing that fits the limit instead of attaching the whole record.
              Aurora doesn't write the filing or guarantee a page count — but it
              gives you a much shorter, organized starting point.
            </p>
          </FaqItem>

          <FaqItem q="Does a person review my analysis before I get it?">
            <p>
              No one on our side reads your case. The "human in the loop" is
              you: every claim links to the exact messages behind it so you, and
              your attorney, can verify it. Aurora doesn't approve or sign off
              on anything — the analysis is yours to check and use as you see
              fit.
            </p>
          </FaqItem>

          <FaqItem q="Does Aurora decide what's important in my case?">
            <p>
              No. It reads your whole record and reports the patterns it finds,
              with citations. What matters for your case, and what goes in front
              of a court, is for you and your attorney to decide.
            </p>
          </FaqItem>

          <FaqItem q="Is this official, court-ready evidence?">
            <p>
              Your messages are the evidence. Aurora's document is a reading aid
              that helps you and your attorney find and organize what's already
              there. It doesn't certify where your messages came from or how
              they were collected — that's a separate, forensic step. How
              anything gets used in a proceeding is up to your attorney and the
              court.
            </p>
          </FaqItem>

          <FaqItem q="Could Aurora make up a pattern, or miss one?">
            <p>
              It won't invent patterns that aren't in your messages — that's why
              every claim is tied to messages you can check. And it isn't
              guaranteed to catch everything: a pattern it doesn't surface isn't
              proof none exists. It's a careful reading aid for a human
              decision-maker, never a diagnosis or a verdict.
            </p>
          </FaqItem>

          <FaqItem q="What happens to my messages?">
            <p>
              They're read in your browser, sent to our AI provider to run the
              analysis, then deleted from our servers the moment your document
              is ready. The provider deletes them within 30 days and never uses
              them to train AI — rarely, a message its safety systems flag may be
              kept a little longer. No one on our staff browses your case content.
              Full detail is on the <a href="/privacy">Privacy page</a>.
            </p>
          </FaqItem>

          <FaqItem q="Can I run it more than once?">
            <p>
              Your access code is good for one walk-through. Do the setup —
              upload your export, name the people, start the run — in one
              sitting, since the code can't be reused once you've stepped
              through it. You don't need to stay after that: we email you when
              your document is ready.
            </p>
          </FaqItem>

          <FaqItem q="What if I lose my file, or close the tab before saving?">
            <p>
              The link in your "analysis ready" email re-downloads your locked
              file for 7 days, from any device. Everything is locked with the
              passphrase you saved when you started — we don't keep a copy of it,
              so even we can't open your file, and after 7 days it's all deleted.
              If you lose the passphrase itself, the file can't be opened — by
              anyone.
            </p>
          </FaqItem>

          <FaqItem q="My record is large, or I got a message asking to email you. What now?">
            <p>
              Some records are bigger than the website can take automatically.
              When that happens Aurora routes you to email us rather than
              running, so we can sort it out with you. Reach us at{" "}
              <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
            </p>
          </FaqItem>

          <FaqItem q="Who do I contact for help, or about my data?">
            <p>
              <a href={`mailto:${CONTACT}`}>{CONTACT}</a> for anything about
              your analysis or getting unstuck.{" "}
              <a href={`mailto:${DATA_CONTACT}`}>{DATA_CONTACT}</a> to access or
              delete the information we hold about you.
            </p>
          </FaqItem>
        </article>

        <footer className="landing-foot">
          <div className="landing-foot-left">
            Aurora · A product of Plain to See LLC.
          </div>
          <div className="landing-foot-right">&copy; 2026 Aurora</div>
        </footer>
      </div>
    </main>
  );
}
